import React, { useEffect, useState } from "react";
import { api, inr, formatApiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line
} from "recharts";
import {
  Target as TargetIcon, Trophy, Wallet, TrendUp, CurrencyInr, Plus, PencilSimple, Trash, ChartLineUp, Fire
} from "@phosphor-icons/react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const EXPENSE_CATEGORIES = [
  "Salary", "Rent", "Utilities", "Travel", "Office Supplies",
  "Marketing", "Software", "Repairs", "Professional Fees", "Other",
];

function OverviewTab({ perf }) {
  if (!perf) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const k = perf.totals;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="kpi">
          <div className="eyebrow flex items-center gap-1.5"><CurrencyInr size={11} /> Sales</div>
          <div className="val">{inr(k.sales)}</div>
          <div className="text-[11px] font-mono mt-1 text-muted-foreground">{k.invoices} invoice(s)</div>
        </div>
        <div className="kpi">
          <div className="eyebrow">Purchases</div>
          <div className="val">{inr(k.purchases)}</div>
        </div>
        <div className="kpi">
          <div className="eyebrow">Expenses</div>
          <div className="val" style={{ color: "hsl(var(--warning))" }}>{inr(k.expenses)}</div>
        </div>
        <div className="kpi">
          <div className="eyebrow flex items-center gap-1.5"><TrendUp size={11} weight="fill" /> Net Profit</div>
          <div className="val" style={{ color: k.profit >= 0 ? "hsl(var(--success))" : "hsl(var(--destructive))" }}>
            {inr(k.profit)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-flat p-6">
          <div className="eyebrow">Daily Breakdown</div>
          <div className="font-heading text-lg font-semibold tracking-tight mt-0.5 mb-4">Sales · Expenses · Profit</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={perf.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} formatter={(v) => inr(v)} />
                <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }} />
                <Bar dataKey="sales" fill="hsl(var(--chart-1))" name="Sales" radius={[3,3,0,0]} />
                <Bar dataKey="expenses" fill="hsl(var(--chart-5))" name="Expenses" radius={[3,3,0,0]} />
                <Bar dataKey="profit" fill="hsl(var(--chart-2))" name="Profit" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat p-6">
          <div className="eyebrow">Expenses</div>
          <div className="font-heading text-lg font-semibold tracking-tight mt-0.5 mb-4">By Category</div>
          {perf.expense_categories.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4">No expenses in this range.</div>
          ) : perf.expense_categories.map((c) => {
            const pct = (c.amount / (k.expenses || 1)) * 100;
            return (
              <div key={c.category} className="mb-3">
                <div className="flex justify-between text-[12.5px] mb-1">
                  <span>{c.category}</span>
                  <span className="font-mono">{inr(c.amount)}</span>
                </div>
                <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, pct)}%` }}></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="eyebrow flex items-center gap-1.5"><Trophy size={11} weight="fill" style={{ color: "hsl(var(--warning))" }} /> Targets & Incentives</div>
            <div className="font-heading text-lg font-semibold tracking-tight mt-0.5">Performance Scoreboard</div>
          </div>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Assignee</th>
              <th>Period</th>
              <th>Type</th>
              <th className="num">Target</th>
              <th className="num">Achieved</th>
              <th style={{ width: "20%" }}>Progress</th>
              <th className="num">Incentive Earned</th>
            </tr>
          </thead>
          <tbody>
            {perf.targets.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">No targets defined yet.</td></tr>
            ) : perf.targets.map((t) => {
              const hit = t.achievement_pct >= 100;
              return (
                <tr key={t.id}>
                  <td className="font-medium">{t.user_name}</td>
                  <td><span className="pill pill-muted">{t.period_type}</span></td>
                  <td className="capitalize">{t.target_type}</td>
                  <td className="num font-mono">{inr(t.target_amount)}</td>
                  <td className="num font-mono font-semibold">{inr(t.achieved)}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${hit ? "bg-success" : "bg-primary"}`}
                          style={{ width: `${Math.min(100, t.achievement_pct)}%`, backgroundColor: hit ? "hsl(var(--success))" : undefined }}></div>
                      </div>
                      <span className={`font-mono text-[11.5px] w-12 text-right ${hit ? "text-success" : ""}`} style={{ color: hit ? "hsl(var(--success))" : undefined }}>
                        {t.achievement_pct.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                  <td className="num font-mono font-semibold" style={{ color: t.incentive_earned > 0 ? "hsl(var(--success))" : undefined }}>
                    {inr(t.incentive_earned)}
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                      {t.incentive_type === "percentage" ? `${t.incentive_value}% of sales` : `Fixed on hit`}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <div className="eyebrow">Team Performance</div>
          <div className="font-heading text-lg font-semibold tracking-tight mt-0.5">Sales by User</div>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>User</th>
              <th className="num">Invoices</th>
              <th className="num">Sales Value</th>
            </tr>
          </thead>
          <tbody>
            {perf.by_user.length === 0 ? (
              <tr><td colSpan={3} className="text-center py-6 text-muted-foreground">No sales in this range.</td></tr>
            ) : perf.by_user.map((u) => (
              <tr key={u.user_id}>
                <td className="font-medium">{u.user_name}</td>
                <td className="num font-mono">{u.invoices}</td>
                <td className="num font-mono font-semibold">{inr(u.sales)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TargetsTab({ reload }) {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    user_id: "", period_type: "monthly",
    period_start: today().slice(0, 8) + "01", period_end: today(),
    target_type: "sales", target_amount: 0,
    incentive_type: "percentage", incentive_value: 2, notes: "",
  });

  const load = async () => {
    const [{ data: t }, { data: u }] = await Promise.all([api.get("/targets"), api.get("/users")]);
    setRows(t);
    setUsers(u.filter((x) => x.active));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, target_amount: Number(form.target_amount) || 0, incentive_value: Number(form.incentive_value) || 0 };
      const u = users.find((x) => x.id === payload.user_id);
      payload.user_name = u?.name || "";
      await api.post("/targets", payload);
      toast.success("Target created");
      setOpen(false); load(); reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this target?")) return;
    try { await api.delete(`/targets/${id}`); toast.success("Deleted"); load(); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const setPreset = (kind) => {
    const now = new Date();
    if (kind === "day") setForm((f) => ({ ...f, period_type: "daily", period_start: today(), period_end: today() }));
    if (kind === "week") setForm((f) => ({ ...f, period_type: "weekly", period_start: daysAgo(now.getDay()), period_end: today() }));
    if (kind === "month") setForm((f) => ({ ...f, period_type: "monthly", period_start: today().slice(0, 8) + "01", period_end: today() }));
    if (kind === "year") setForm((f) => ({ ...f, period_type: "yearly", period_start: today().slice(0, 5) + "01-01", period_end: today() }));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">Define sales / collection / profit targets per user (or company-wide). Incentives compute automatically.</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-9" data-testid="new-target-btn"><Plus size={14} className="mr-1.5" /> New Target</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="font-heading tracking-tight">New Target</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4">
              <div className="col-span-2 flex gap-2">
                {["day","week","month","year"].map((k) => (
                  <Button type="button" key={k} variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setPreset(k)}>{k}</Button>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Assignee</Label>
                <Select value={form.user_id || "company"} onValueChange={(v) => setForm({ ...form, user_id: v === "company" ? "" : v })}>
                  <SelectTrigger data-testid="target-assignee"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Company-wide</SelectItem>
                    {users.filter((u) => u.role === "manager").length > 0 && (
                      <div className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-t border-border mt-1">Managers</div>
                    )}
                    {users.filter((u) => u.role === "manager").map((u) => (
                      <SelectItem key={u.id} value={u.id} data-testid={`target-assignee-${u.id}`}>
                        {u.name} <span className="text-muted-foreground">· manager</span>
                      </SelectItem>
                    ))}
                    {users.filter((u) => u.role === "employee").length > 0 && (
                      <div className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-t border-border mt-1">Employees</div>
                    )}
                    {users.filter((u) => u.role === "employee").map((u) => (
                      <SelectItem key={u.id} value={u.id} data-testid={`target-assignee-${u.id}`}>
                        {u.name} <span className="text-muted-foreground">· employee</span>
                      </SelectItem>
                    ))}
                    {users.filter((u) => u.role === "admin").length > 0 && (
                      <div className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-t border-border mt-1">Admins</div>
                    )}
                    {users.filter((u) => u.role === "admin").map((u) => (
                      <SelectItem key={u.id} value={u.id} data-testid={`target-assignee-${u.id}`}>
                        {u.name} <span className="text-muted-foreground">· admin</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Target Type</Label>
                <Select value={form.target_type} onValueChange={(v) => setForm({ ...form, target_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sales Revenue</SelectItem>
                    <SelectItem value="collection">Collection</SelectItem>
                    <SelectItem value="profit">Profit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Period Type</Label>
                <Select value={form.period_type} onValueChange={(v) => setForm({ ...form, period_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Target Amount (₹) *</Label>
                <Input required type="number" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} className="font-mono" data-testid="target-amount" />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Period Start</Label>
                <Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Period End</Label>
                <Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Incentive Type</Label>
                <Select value={form.incentive_type} onValueChange={(v) => setForm({ ...form, incentive_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage of achieved</SelectItem>
                    <SelectItem value="fixed">Fixed ₹ on target hit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Incentive {form.incentive_type === "percentage" ? "(%)" : "(₹)"}</Label>
                <Input type="number" step="0.01" value={form.incentive_value} onChange={(e) => setForm({ ...form, incentive_value: e.target.value })} className="font-mono" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="eyebrow">Notes</Label>
                <Textarea rows={1} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <DialogFooter className="col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="target-save-btn">Create Target</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Assignee</th>
              <th>Type</th>
              <th>Period</th>
              <th>Range</th>
              <th className="num">Target</th>
              <th>Incentive</th>
              <th className="num" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">No targets yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`target-row-${r.id}`}>
                <td className="font-medium">{r.user_name || "Company-wide"}</td>
                <td className="capitalize">{r.target_type}</td>
                <td><span className="pill pill-muted">{r.period_type}</span></td>
                <td className="font-mono text-[12px] text-muted-foreground">{r.period_start} → {r.period_end}</td>
                <td className="num font-mono font-semibold">{inr(r.target_amount)}</td>
                <td className="font-mono text-[12px]">{r.incentive_type === "percentage" ? `${r.incentive_value}% of sales` : `${inr(r.incentive_value)} on hit`}</td>
                <td className="num" style={{ textAlign: "right" }}>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(r.id)} data-testid={`target-del-${r.id}`}>
                    <Trash size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExpensesTab({ reload }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: today(), category: "Salary", amount: 0, payment_mode: "cash",
    reference: "", vendor_name: "", notes: "",
  });

  const load = async () => {
    const { data } = await api.get("/expenses");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) || 0 });
      toast.success("Expense recorded");
      setOpen(false); load(); reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (id) => {
    if (!confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          Total {rows.length} expense(s) · <span className="font-mono font-semibold">{inr(total)}</span>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-9" data-testid="new-expense-btn"><Plus size={14} className="mr-1.5" /> New Expense</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="font-heading tracking-tight">New Expense</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="eyebrow">Date *</Label>
                <Input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Amount (₹) *</Label>
                <Input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="font-mono" data-testid="expense-amount" />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Payment Mode</Label>
                <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="neft">NEFT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Vendor</Label>
                <Input value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="eyebrow">Reference</Label>
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="font-mono" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="eyebrow">Notes</Label>
                <Textarea rows={1} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <DialogFooter className="col-span-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="expense-save-btn">Record Expense</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Category</th>
              <th>Vendor</th>
              <th>Mode</th>
              <th>Reference</th>
              <th className="num">Amount</th>
              <th className="num" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">No expenses yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`expense-row-${r.id}`}>
                <td className="font-mono text-[12px]">{fmtDate(r.date)}</td>
                <td className="font-medium">{r.category}</td>
                <td>{r.vendor_name || "—"}</td>
                <td><span className="pill pill-muted">{r.payment_mode}</span></td>
                <td className="font-mono text-[12px] text-muted-foreground">{r.reference || "—"}</td>
                <td className="num font-mono font-semibold text-warning" style={{ color: "hsl(var(--warning))" }}>{inr(r.amount)}</td>
                <td className="num" style={{ textAlign: "right" }}>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(r.id)} data-testid={`expense-del-${r.id}`}>
                    <Trash size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Performance() {
  const { activeCompanyId, activeStoreId } = useAuth();
  const [period, setPeriod] = useState("month");
  const [perf, setPerf] = useState(null);

  const load = async () => {
    if (!activeCompanyId) return;
    const { data } = await api.get(`/performance?period=${period}`);
    setPerf(data);
  };
  useEffect(() => { load(); }, [activeCompanyId, activeStoreId, period]);

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Analytics</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <ChartLineUp size={26} weight="fill" className="text-primary" />
            Performance
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track daily sales, expenses, profit, targets and staff incentives.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[
            { k: "day", l: "Today" },
            { k: "week", l: "This Week" },
            { k: "month", l: "This Month" },
            { k: "year", l: "This Year" },
          ].map((p) => (
            <Button key={p.k} variant={period === p.k ? "default" : "outline"}
              onClick={() => setPeriod(p.k)}
              className="h-9" data-testid={`period-${p.k}`}>
              {p.l}
            </Button>
          ))}
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList data-testid="perf-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview"><TrendUp size={13} className="mr-1.5" /> Overview</TabsTrigger>
          <TabsTrigger value="targets" data-testid="tab-targets"><TargetIcon size={13} className="mr-1.5" /> Targets & Incentives</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses"><Wallet size={13} className="mr-1.5" /> Expenses</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5">
          <OverviewTab perf={perf} />
        </TabsContent>
        <TabsContent value="targets" className="mt-5">
          <TargetsTab reload={load} />
        </TabsContent>
        <TabsContent value="expenses" className="mt-5">
          <ExpensesTab reload={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
